import { useEffect, useRef, useState } from "react";

const setLast = (m, text) => { const c = [...m]; const last = c[c.length - 1]; c[c.length - 1] = { role: "assistant", text: typeof text === "function" ? text(last?.role === "assistant" ? last.text : "") : text }; return c; };
import { Loader2, SendHorizontal, Sparkles } from "lucide-react";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { streamAi } from "../../lib/aiStream";

const sessionFor = (key) => {
  const k = `vavlis.ai.${key}`;
  let id = localStorage.getItem(k);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(k, id); }
  return id;
};

// Streaming assistant panel; `path` is the backend ask endpoint, `suggestions` are tap-to-ask chips
const Rich = ({ text }) => text.split("\n").map((line, i) => {
  const bullet = /^\s*([-•*]|\d+\.)\s+/.test(line);
  const parts = line.replace(/^\s*([-•*]|\d+\.)\s+/, "").split(/(\*\*[^*]+\*\*)/g).map((p, j) => (p.startsWith("**") ? <b key={j}>{p.slice(2, -2)}</b> : p));
  return <div key={i} className={bullet ? "flex gap-2 pl-1" : ""}>{bullet && <span>•</span>}<span>{parts}</span></div>;
});

export const AiChat = ({ path, sessionKey, suggestions = [], testId = "ai-chat" }) => {
  const { t } = useI18n();
  const [sid] = useState(() => sessionFor(sessionKey));
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const asked = useRef(false);
  useEffect(() => {
    let live = true;
    api.get(`/ai/history/${sid}`).then((r) => { if (live && !asked.current && Array.isArray(r.data)) setMsgs(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [sid]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question || busy) return;
    setQ("");
    setBusy(true);
    asked.current = true;
    setMsgs((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    try {
      await streamAi(path, { session_id: sid, question }, (chunk) => setMsgs((m) => setLast(m, (prev) => prev + chunk)));
    } catch (e) {
      setMsgs((m) => setLast(m, t("ai.error")));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-[70vh] flex-col rounded-2xl bg-white shadow-sm" data-testid={testId}>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid={`${testId}-messages`}>
        {msgs.length === 0 && (
          <div className="space-y-3 text-sm text-slate-500">
            <p className="flex items-center gap-2 text-navy"><Sparkles className="h-4 w-4 text-brand" /> {t("ai.intro")}</p>
            <div className="flex flex-wrap gap-2">{suggestions.map((s) => <button key={s} type="button" onClick={() => ask(s)} className="h-9 rounded-full bg-canvas px-3 text-xs font-medium text-navy hover:bg-slate-200" data-testid={`${testId}-suggestion`}>{s}</button>)}</div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-navy text-white" : "bg-canvas text-navy"}`} data-testid={`${testId}-${m.role}`}>
              {m.text ? <Rich text={m.text} /> : <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 border-t border-slate-100 p-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ai.placeholder")} className="h-12 min-w-0 flex-1 rounded-full bg-canvas px-4 text-base text-navy outline-none focus:ring-4 focus:ring-brand/20" data-testid={`${testId}-input`} />
        <button type="submit" disabled={busy || !q.trim()} className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-brand text-navy disabled:opacity-50" aria-label={t("ai.send")} data-testid={`${testId}-send`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <SendHorizontal className="h-5 w-5" />}
        </button>
      </form>
    </div>
  );
};
